// ============================================================================
// TEXT UTILITY FUNCTIONS
// ============================================================================
// Utility functions for text processing, word counting, and text chunking

import { PAGE_BREAK_TOKEN } from './constants'
import { isAbbreviationPeriod } from './sentence-breaks'
import { DEFAULT_SPEECH_LANGUAGE, type SpeechLanguage } from './speech-language'

// Maximum characters per utterance (browsers typically limit to 200-300, using 2000 for safety)
export const MAX_UTTERANCE_LENGTH = 2000

// Maximum sentences per chunk for better speech synthesis and highlighting
export const MAX_SENTENCES_PER_CHUNK = 3

// ============================================================================
// SPEECH CHUNK SIZE
// ============================================================================
// A chunk is one utterance and one highlight unit. Whole sentences were both:
// they routinely ran to thirty or forty words after line joining, which the
// external review called out as far too much text to mark at once, and on the
// devices whose speech engine reports no word boundaries the chunk is all the
// reader gets.
//
// Sentences longer than MAX are therefore cut at their own punctuation - commas,
// semicolons and colons, all places a reader pauses anyway - aiming for TARGET
// words per piece and never leaving a tail shorter than MIN. A sentence with no
// internal punctuation is left whole on purpose: cutting at an arbitrary word
// would damage the prosody more than a long highlight hurts.
export const MAX_CHUNK_WORDS = 12
export const TARGET_CHUNK_WORDS = 8
export const MIN_CHUNK_WORDS = 4

// ============================================================================
// NUMBER AND DATE PROCESSING FOR TTS
// ============================================================================

function isDigit(character: string | undefined): boolean {
  return character !== undefined && character >= '0' && character <= '9'
}

/**
 * Polish digit names for TTS
 */
const POLISH_DIGITS: Record<string, string> = {
  '0': 'zero',
  '1': 'jeden',
  '2': 'dwa',
  '3': 'trzy',
  '4': 'cztery',
  '5': 'pięć',
  '6': 'sześć',
  '7': 'siedem',
  '8': 'osiem',
  '9': 'dziewięć'
}

/**
 * Polish month names (genitive case for dates)
 */
const POLISH_MONTHS: Record<number, string> = {
  1: 'stycznia',
  2: 'lutego',
  3: 'marca',
  4: 'kwietnia',
  5: 'maja',
  6: 'czerwca',
  7: 'lipca',
  8: 'sierpnia',
  9: 'września',
  10: 'października',
  11: 'listopada',
  12: 'grudnia'
}

/**
 * Polish ordinal numbers for days (1-31)
 */
const POLISH_ORDINAL_DAYS: Record<number, string> = {
  1: 'pierwszego',
  2: 'drugiego',
  3: 'trzeciego',
  4: 'czwartego',
  5: 'piątego',
  6: 'szóstego',
  7: 'siódmego',
  8: 'ósmego',
  9: 'dziewiątego',
  10: 'dziesiątego',
  11: 'jedenastego',
  12: 'dwunastego',
  13: 'trzynastego',
  14: 'czternastego',
  15: 'piętnastego',
  16: 'szesnastego',
  17: 'siedemnastego',
  18: 'osiemnastego',
  19: 'dziewiętnastego',
  20: 'dwudziestego',
  21: 'dwudziestego pierwszego',
  22: 'dwudziestego drugiego',
  23: 'dwudziestego trzeciego',
  24: 'dwudziestego czwartego',
  25: 'dwudziestego piątego',
  26: 'dwudziestego szóstego',
  27: 'dwudziestego siódmego',
  28: 'dwudziestego ósmego',
  29: 'dwudziestego dziewiątego',
  30: 'trzydziestego',
  31: 'trzydziestego pierwszego'
}

/**
 * Polish cardinal numbers for list numbering (1-20)
 */
const POLISH_CARDINALS: Record<number, string> = {
  1: 'jeden',
  2: 'dwa',
  3: 'trzy',
  4: 'cztery',
  5: 'pięć',
  6: 'sześć',
  7: 'siedem',
  8: 'osiem',
  9: 'dziewięć',
  10: 'dziesięć',
  11: 'jedenaście',
  12: 'dwanaście',
  13: 'trzynaście',
  14: 'czternaście',
  15: 'piętnaście',
  16: 'szesnaście',
  17: 'siedemnaście',
  18: 'osiemnaście',
  19: 'dziewiętnaście',
  20: 'dwadzieścia',
}

/**
 * Common Polish abbreviations and their full forms
 */
const POLISH_ABBREVIATIONS: Record<string, string> = {
  // Titles
  'dr': 'doktor',
  'dr.': 'doktor',
  'mgr': 'magister',
  'mgr.': 'magister',
  'inż': 'inżynier',
  'inż.': 'inżynier',
  'prof': 'profesor',
  'prof.': 'profesor',
  'doc': 'docent',
  'doc.': 'docent',
  
  // Address
  'ul': 'ulica',
  'ul.': 'ulica',
  'al': 'aleja',
  'al.': 'aleja',
  'pl': 'plac',
  'pl.': 'plac',
  'os': 'osiedle',
  'os.': 'osiedle',
  'nr': 'numer',
  'nr.': 'numer',
  
  // Common
  'np': 'na przykład',
  'np.': 'na przykład',
  'itd': 'i tak dalej',
  'itd.': 'i tak dalej',
  'itp': 'i tym podobne',
  'itp.': 'i tym podobne',
  'tj': 'to jest',
  'tj.': 'to jest',
  'tzw': 'tak zwany',
  'tzw.': 'tak zwany',
  'ok': 'około',
  'ok.': 'około',
  'wg': 'według',
  'wg.': 'według',
  'jw': 'jak wyżej',
  'jw.': 'jak wyżej',
  'cdn': 'ciąg dalszy nastąpi',
  'cdn.': 'ciąg dalszy nastąpi',
  
  // Time
  'godz': 'godzina',
  'godz.': 'godzina',
  'min': 'minut',
  'min.': 'minut',
  'sek': 'sekund',
  'sek.': 'sekund',
  
  // Units (NOTE: single-letter 'g' and 'l' removed - JS \b doesn't handle Polish chars)
  'cm': 'centymetrów',
  'mm': 'milimetrów',
  'km': 'kilometrów',
  'kg': 'kilogramów',
  'mg': 'miligramów',
  'ml': 'mililitrów',
  'zł': 'złotych',
  'gr': 'groszy',
  
  // Days
  'pon': 'poniedziałek',
  'pon.': 'poniedziałek',
  'wt': 'wtorek',
  'wt.': 'wtorek',
  'śr': 'środa',
  'śr.': 'środa',
  'czw': 'czwartek',
  'czw.': 'czwartek',
  'pt': 'piątek',
  'pt.': 'piątek',
  'sob': 'sobota',
  'sob.': 'sobota',
  'niedz': 'niedziela',
  'niedz.': 'niedziela',
  'ndz': 'niedziela',
  'ndz.': 'niedziela',
  
  // Months short
  'sty': 'stycznia',
  'sty.': 'stycznia',
  'lut': 'lutego',
  'lut.': 'lutego',
  'mar': 'marca',
  'mar.': 'marca',
  'kwi': 'kwietnia',
  'kwi.': 'kwietnia',
  'maj': 'maja',
  'cze': 'czerwca',
  'cze.': 'czerwca',
  'lip': 'lipca',
  'lip.': 'lipca',
  'sie': 'sierpnia',
  'sie.': 'sierpnia',
  'wrz': 'września',
  'wrz.': 'września',
  'paź': 'października',
  'paź.': 'października',
  'lis': 'listopada',
  'lis.': 'listopada',
  'gru': 'grudnia',
  'gru.': 'grudnia',
  
  // Other
  'tel': 'telefon',
  'tel.': 'telefon',
  'fax': 'faks',
  'fax.': 'faks',
  'str': 'strona',
  'str.': 'strona',
  'pkt': 'punkt',
  'pkt.': 'punkt',
  'r.': 'roku'
}

/**
 * Convert digits to Polish words for TTS
 * Used for phone numbers and similar sequences
 */
function digitsToPolish(digits: string): string {
  return digits
    .split('')
    .map(d => POLISH_DIGITS[d] || d)
    .join(' ')
}

/**
 * Convert a year to Polish words
 */
function yearToPolish(year: number): string {
  if (year >= 2000 && year <= 2099) {
    const ones = year % 10
    const tens = Math.floor((year % 100) / 10)
    
    if (year === 2000) return 'dwutysięcznym'
    
    const tensWords: Record<number, string> = {
      0: '',
      1: 'dziesięć',
      2: 'dwadzieścia',
      3: 'trzydzieści',
      4: 'czterdzieści',
      5: 'pięćdziesiąt',
      6: 'sześćdziesiąt',
      7: 'siedemdziesiąt',
      8: 'osiemdziesiąt',
      9: 'dziewięćdziesiąt'
    }
    
    const onesWords: Record<number, string> = {
      0: '',
      1: 'jeden',
      2: 'dwa',
      3: 'trzy',
      4: 'cztery',
      5: 'pięć',
      6: 'sześć',
      7: 'siedem',
      8: 'osiem',
      9: 'dziewięć'
    }
    
    // Special cases for 11-19
    const teensWords: Record<number, string> = {
      10: 'dziesiątym',
      11: 'jedenastym',
      12: 'dwunastym',
      13: 'trzynastym',
      14: 'czternastym',
      15: 'piętnastym',
      16: 'szesnastym',
      17: 'siedemnastym',
      18: 'osiemnastym',
      19: 'dziewiętnastym'
    }
    
    const twoDigit = year % 100
    if (twoDigit >= 10 && twoDigit <= 19) {
      return `dwa tysiące ${teensWords[twoDigit]}`
    }
    
    let result = 'dwa tysiące'
    if (tens > 0) result += ` ${tensWords[tens]}`
    if (ones > 0) result += ` ${onesWords[ones]}`
    
    return result.trim()
  }
  
  // For years outside 2000-2099, just read as number
  return String(year)
}

/**
 * Convert date format DD.MM.YYYY or DD/MM/YYYY or DD-MM-YYYY to Polish
 */
function dateToPolish(day: number, month: number, year: number): string {
  const dayText = POLISH_ORDINAL_DAYS[day] || String(day)
  const monthText = POLISH_MONTHS[month] || String(month)
  const yearText = yearToPolish(year)
  
  return `${dayText} ${monthText} ${yearText}`
}

/**
 * Detect and convert phone numbers to digit-by-digit reading
 * Patterns: 
 * - 123 456 789 (groups of 3)
 * - 12 345 67 89 (mixed groups)
 * - +48 123 456 789 (with country code)
 * - 123-456-789 (with dashes)
 */
function convertPhoneNumbers(text: string): string {
  // Phone with country code: +48 123 456 789 or +48 123-456-789
  text = text.replace(/\+(\d{1,3})[\s-](\d{2,4})[\s-](\d{2,4})[\s-](\d{2,4})/g, 
    (_, country, g1, g2, g3) => {
      return `plus ${digitsToPolish(country)}, ${digitsToPolish(g1)}, ${digitsToPolish(g2)}, ${digitsToPolish(g3)}`
    }
  )
  
  // 9-digit phone: 123 456 789 or 123-456-789
  text = text.replace(/\b(\d{3})[\s-](\d{3})[\s-](\d{3})\b/g,
    (_, g1, g2, g3) => `${digitsToPolish(g1)}, ${digitsToPolish(g2)}, ${digitsToPolish(g3)}`
  )
  
  // Long number sequences with spaces (likely phone/ID): 12 34 56 78 or 123 45 678
  text = text.replace(/\b(\d{2,4})[\s-](\d{2,4})[\s-](\d{2,4})\b/g,
    (_, g1, g2, g3) => `${digitsToPolish(g1)}, ${digitsToPolish(g2)}, ${digitsToPolish(g3)}`
  )
  
  // Two groups: 123 456 or 1234 5678
  text = text.replace(/\b(\d{3,4})[\s-](\d{3,4})\b/g,
    (_, g1, g2) => `${digitsToPolish(g1)}, ${digitsToPolish(g2)}`
  )
  
  return text
}

function hasLeadingZero(numStr: string): boolean {
  return numStr.length > 1 && numStr.startsWith('0')
}

function isInMathOrChainedDotContext(
  fullText: string,
  offset: number,
  matchLength: number
): boolean {
  const before = fullText.slice(Math.max(0, offset - 3), offset)
  const after = fullText.slice(offset + matchLength, offset + matchLength + 3)
  const mathPattern = /[+\-×÷=<>]/

  if (mathPattern.test(before) || mathPattern.test(after)) {
    return true
  }

  // Chained dots like 1.2.3 — not a short date
  return /^\.\d/.test(after)
}

function isLikelyShortDate(
  day: number,
  month: number,
  dayStr: string,
  monthStr: string,
  fullText: string,
  offset: number,
  matchLength: number
): boolean {
  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return false
  }

  if (isInMathOrChainedDotContext(fullText, offset, matchLength)) {
    return false
  }

  // Ambiguous decimal vs day.month: both <= 12 with no leading zeros
  if (day <= 12 && month <= 12 && !hasLeadingZero(dayStr) && !hasLeadingZero(monthStr)) {
    return false
  }

  return true
}

/**
 * Convert line-start list numbering to Polish cardinal words for TTS
 * e.g. "1. Oblicz" -> "jeden. Oblicz", "2) Zadanie" -> "dwa) Zadanie"
 */
function convertListNumbering(text: string): string {
  return text.replace(
    /^(\d{1,2})([.)])\s+(?=[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż])/gm,
    (match, num, separator) => {
    const n = parseInt(num, 10)
    const cardinal = POLISH_CARDINALS[n]
    if (cardinal) {
      return `${cardinal}${separator} `
    }
    return match
  })
}

/**
 * Convert dates to Polish readable format
 * Patterns: DD.MM.YYYY, DD/MM/YYYY, DD-MM-YYYY
 */
function convertDates(text: string): string {
  // Full date: DD.MM.YYYY or DD/MM/YYYY or DD-MM-YYYY
  text = text.replace(/\b(\d{1,2})[./-](\d{1,2})[./-](\d{4})\b/g, 
    (_, day, month, year) => {
      const d = parseInt(day, 10)
      const m = parseInt(month, 10)
      const y = parseInt(year, 10)
      
      if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= 2100) {
        return dateToPolish(d, m, y)
      }
      return _  // Return original if not a valid date
    }
  )
  
  // Short date: DD.MM or DD/MM (current year implied)
  text = text.replace(/\b(\d{1,2})[./](\d{1,2})\b(?!\d)/g,
    (match, dayStr, monthStr, offset, fullText) => {
      const d = parseInt(dayStr, 10)
      const m = parseInt(monthStr, 10)

      if (!isLikelyShortDate(d, m, dayStr, monthStr, fullText, offset, match.length)) {
        return match
      }

      const dayText = POLISH_ORDINAL_DAYS[d] || String(d)
      const monthText = POLISH_MONTHS[m] || String(m)
      return `${dayText} ${monthText}`
    }
  )
  
  return text
}

/** Whether the text just before the match already says "godzina" or "godz.". */
function hasHourWordBefore(text: string, offset: number): boolean {
  return /godz\w*\.?\s*$/i.test(text.slice(Math.max(0, offset - 12), offset))
}

/**
 * Convert time format HH:MM to Polish
 */
function convertTime(text: string): string {
  // Time: HH:MM or H:MM
  text = text.replace(/\b(\d{1,2}):(\d{2})\b/g,
    (match, hours, minutes, offset: number, fullText: string) => {
      const h = parseInt(hours, 10)
      const m = parseInt(minutes, 10)

      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        if (m === 0) {
          // "od godz. 10:00" would otherwise become "od godzina godzina 10" once
          // the abbreviation expansion below turns "godz." into a word of its own.
          return hasHourWordBefore(fullText, offset) ? `${h}` : `godzina ${h}`
        }
        return `${h} ${m}`
      }
      return match
    }
  )
  
  return text
}

/**
 * One word as the expander sees it: letters, digits, and the three marks that
 * appear inside abbreviations themselves ('.', '-', '%').
 *
 * The Latin range covers the Polish diacritics, which all sit in U+00C0-U+024F.
 * The same class is used by sentence-breaks.ts for the same reason.
 */
const ABBREVIATION_TOKEN = /[0-9A-Za-zÀ-ɏ.%-]+/g

const ABBREVIATION_LOOKUP = new Map(
  Object.entries(POLISH_ABBREVIATIONS).map(([abbreviation, expansion]) => [
    abbreviation.toLowerCase(),
    expansion,
  ])
)

/**
 * Expand Polish abbreviations.
 *
 * Whole tokens are looked up rather than matched with `\b...\b`, because that
 * boundary does not know about Polish letters and quietly mangled ordinary words:
 * every diacritic counts as a non-word character to it, so "wśród" contained a
 * "śr" with boundaries on both sides and was spoken as "wśrodaód". "grób" became
 * "groszyób", "mgła" became "miligramówła", and "Alę" became "alejaę". The
 * existing note about dropping single-letter 'g' and 'l' was this same bug, found
 * once and patched at one entry instead of at the mechanism.
 *
 * Deliberately no lookbehind assertion, which would express this directly: it
 * crashes JavaScriptCore before Safari 16.4, and this app has already taken that
 * outage once (see sentence-breaks.ts).
 */
function expandAbbreviations(text: string): string {
  return text.replace(ABBREVIATION_TOKEN, (token) => {
    return ABBREVIATION_LOOKUP.get(token.toLowerCase()) ?? token
  })
}

/**
 * Convert standalone long numbers (IDs, codes) to digit-by-digit
 * Only for numbers that look like codes/IDs (5+ digits without spaces)
 */
function convertLongNumbers(text: string): string {
  // Long standalone numbers (5+ digits, likely IDs/codes)
  text = text.replace(/\b(\d{5,})\b/g, (match) => {
    // Don't convert years (4 digits starting with 19 or 20)
    if (match.length === 4 && (match.startsWith('19') || match.startsWith('20'))) {
      return match
    }
    return digitsToPolish(match)
  })
  
  return text
}

// ============================================================================
// TEXT MARKER HANDLING (for TTS and text processing)
// ============================================================================

/**
 * Insert a space after punctuation when missing, but preserve the marks that are
 * part of a number rather than between two words.
 *
 * Dots were already spared for decimals and dates ("1.2", "01.03.2024"). Commas
 * and colons were not, and both carry numbers in Polish text: a decimal comma
 * ("2,5 kg") came out as "2, 5" and a time ("10:00") as "10: 00", which the engine
 * then reads as two separate numbers. Splitting the time also hid it from
 * convertTime below, which runs later and needs the colon intact.
 *
 * The digit test uses the match offset rather than a lookbehind, which crashes
 * JavaScriptCore before Safari 16.4 - the same constraint as sentence-breaks.ts.
 */
function insertSpaceAfterPunctuation(text: string): string {
  const withSpacedDots = text.replace(/\.([^\s\d])/g, '. $1')

  return withSpacedDots.replace(
    /([,!?:;])([^\s])/g,
    (match, mark: string, next: string, offset: number) => {
      const isNumberSeparator = mark === ',' || mark === ':'
      if (isNumberSeparator && isDigit(withSpacedDots[offset - 1]) && isDigit(next)) {
        return match
      }
      return `${mark} ${next}`
    }
  )
}

/**
 * Lightly fixes common OCR artefacts before deeper TTS cleaning.
 * Targets conservative patterns that are unlikely to be intentional:
 * - Missing spaces after punctuation (e.g. "bezowocna,głupia" -> "bezowocna, głupia")
 * - Missing spaces between a lowercase and an uppercase letter in the middle of text
 *   (e.g. "głupiaLudzka" -> "głupia Ludzka")
 */
function fixOcrArtifacts(text: string): string {
  let fixed = insertSpaceAfterPunctuation(text)

  // Insert a space between a lowercase Polish letter and an uppercase Polish letter
  // in the same token – this often indicates a glued word from OCR.
  fixed = fixed.replace(
    /([a-ząćęłńóśźż])([A-ZĄĆĘŁŃÓŚŹŻ])/g,
    '$1 $2'
  )

  return fixed
}

/**
 * What the [OBRAZ] block becomes, so the listener knows the words came off a
 * diagram rather than the running text. Localised because the sentence is spoken
 * by the same voice as the document around it.
 */
const IMAGE_TEXT_PREFIX: Record<SpeechLanguage, string> = {
  pl: 'Tekst z obrazu:',
  en: 'Text from the image:',
  de: 'Text aus dem Bild:',
}

/**
 * Cleans text for TTS by removing or transforming OCR markers:
 * - [OBRAZ] ... [/OBRAZ] -> "Tekst z obrazu: ..." (indicates text came from a diagram/image)
 * - ## text ## -> just "text" (removes heading markers)
 * - ### text ### -> just "text" (removes subheading markers)
 * - * text -> just "text" (removes bullet markers for natural speech)
 *
 * Also transforms numbers, dates, and abbreviations for natural speech:
 * - Phone numbers read digit-by-digit
 * - Dates read in Polish format
 * - Common abbreviations expanded
 */
export function cleanTextForTTS(
  text: string,
  language: SpeechLanguage = DEFAULT_SPEECH_LANGUAGE
): string {
  // First, fix conservative OCR artefacts so that later steps
  // (dates, numbers, abbreviations) see a cleaner input.
  let cleaned = fixOcrArtifacts(text)

  // Transform [OBRAZ] blocks - add verbal prefix so listener knows this text came from an image
  cleaned = cleaned.replace(
    /\[OBRAZ\]\s*([\s\S]*?)\s*\[\/OBRAZ\]/g,
    `${IMAGE_TEXT_PREFIX[language] ?? IMAGE_TEXT_PREFIX[DEFAULT_SPEECH_LANGUAGE]} $1.`
  )
  
  // Remove heading markers but keep the content
  // Handle various formats:
  // - ### text ### (proper format)
  // - ### text (missing closing)
  // - ### text #### (mismatched)
  // - ## text ## (main heading)
  // - ## text (missing closing)
  
  // First, remove any standalone # symbols at end of line (like ####)
  cleaned = cleaned.replace(/\s*#{2,}\s*$/gm, '')
  
  // Subheadings: ### at start (with optional ### at end)
  cleaned = cleaned.replace(/^###\s*(.+?)(?:\s*###)?$/gm, '$1')
  
  // Main headings: ## at start (with optional ## at end)
  cleaned = cleaned.replace(/^##\s*(.+?)(?:\s*##)?$/gm, '$1')
  
  // Remove any remaining # at start of lines (catch-all for edge cases)
  cleaned = cleaned.replace(/^#+\s*/gm, '')
  
  // Remove bullet markers but keep the content
  // Matches lines starting with * followed by space and text
  cleaned = cleaned.replace(/^\*\s+(.+)$/gm, '$1')

  /*
   * Remove inline emphasis markers but keep the words.
   *
   * The reader renders **span** as weight; the speech engine has no use for the
   * asterisks and would pronounce them. Runs after the bullet rule on purpose -
   * that pattern needs whitespace after the asterisk, which `**` never has, so the
   * order is safe either way, but bullets are line-level and this is not.
   */
  cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1')

  /*
   * Remove page break tokens wherever they are, not only on a line of their own.
   *
   * `isSkippableLine` drops a whole line equal to the token, which is how these
   * were kept out of speech. But normalization joins wrapped lines - including
   * across a page boundary, measured 2026-08-25 - and the token then ends up
   * mid-sentence: "...rozwiać wszelkie <STRONA> wątpliwości i pokazać...". A
   * line-equality check cannot see that, the whole sentence reaches the engine, and
   * the listener hears "strona" spoken in the middle of it.
   *
   * The reader is unaffected either way: it splits on the token wherever it sits.
   * This is the speech half of the same in-band signalling problem - see
   * docs/task-p1.6-page-breaks.md.
   */
  cleaned = cleaned.split(PAGE_BREAK_TOKEN).join(' ').replace(/[ \t]{2,}/g, ' ')
  
  /*
   * Everything below rewrites numbers, dates and abbreviations into Polish
   * words, so it may only run on Polish text.
   *
   * It used to run unconditionally, which quietly corrupted every non-Polish
   * document the OCR is explicitly allowed to return (app/api/ocr/route.ts keeps
   * the source language): an English "min." came out as "minut", "10 min" as
   * "10 minut", and 01.03.2024 as "pierwszego marca dwa tysiące dwadzieścia
   * cztery" in the middle of an English sentence. For other languages the engine
   * reads the digits itself, in its own language, which is what we want.
   */
  if (language === 'pl') {
    // Convert phone numbers FIRST (before dates, as they may overlap in patterns)
    cleaned = convertPhoneNumbers(cleaned)

    // Convert list numbering before dates to avoid false date matches
    cleaned = convertListNumbering(cleaned)

    // Convert dates to Polish readable format
    cleaned = convertDates(cleaned)

    // Convert time to Polish readable format
    cleaned = convertTime(cleaned)

    // Convert long standalone numbers to digit-by-digit
    cleaned = convertLongNumbers(cleaned)

    // Expand common abbreviations
    cleaned = expandAbbreviations(cleaned)
  }

  // ---------------------------------------------------------------------------
  // Final whitespace / punctuation normalization for TTS
  // ---------------------------------------------------------------------------
  // At this point abbreviations have already been expanded and numeric/date
  // conversions applied, so it is safer to normalize spacing.

  // Ensure there is exactly one space after basic punctuation when followed
  // by a letter (defensive double-pass in case earlier steps reintroduced
  // tight punctuation). Decimal/date dots are preserved.
  cleaned = insertSpaceAfterPunctuation(cleaned)

  // Collapse multiple spaces (including mixed whitespace) into a single space.
  // Newlines are not needed by the speech engine and can be treated as spaces.
  cleaned = cleaned.replace(/\s+/g, ' ')

  // Trim stray leading/trailing spaces that might affect pronunciation pauses.
  cleaned = cleaned.trim()

  return cleaned
}

/**
 * Checks if a line is purely a marker that should be skipped in sentence processing
 */
function isSkippableLine(line: string): boolean {
  // Skip page break tokens
  if (line === PAGE_BREAK_TOKEN) return true
  
  // Skip empty image markers (shouldn't happen, but safety check)
  if (line === '[OBRAZ]' || line === '[/OBRAZ]') return true
  
  return false
}

// ============================================================================
// SENTENCE BOUNDARY DETECTION
// ============================================================================

export interface SentenceBoundary {
  startCharIndex: number
  endCharIndex: number
  startWordIndex: number
  endWordIndex: number
  text: string      // Original text for display
  ttsText: string   // Cleaned text for speech synthesis (computed lazily)
}

// Removed SentenceChunk interface - now using one utterance per sentence

interface CharRange {
  start: number
  end: number
}

const SENTENCE_TERMINATORS = '.!?'
const PHRASE_SEPARATORS = ',;:'

/** Marks that belong to the sentence they follow rather than the next one. */
const TRAILING_PUNCTUATION = '"\'\u201d\u2019)]\u00bb'

const WHITESPACE_CHAR = /\s/

/**
 * A period with digits on both sides belongs to a number, not to a sentence.
 *
 * Without this, "1.5" and "01.03.2024" were split into two and three chunks, so
 * the date and decimal handling in cleanTextForTTS - which runs per chunk - never
 * saw a whole date and could not fire at all.
 */
function isNumericPeriod(text: string, index: number): boolean {
  return isDigit(text[index - 1]) && isDigit(text[index + 1])
}

/**
 * True for the period in a list marker - the "1." of "1. Oblicz pole".
 *
 * Splitting there left the number alone as its own utterance, spoken as a bare
 * "one" before the item, and it also hid the marker from the list-numbering rule
 * in cleanTextForTTS, which needs to see the item text following it.
 */
function isListNumberPeriod(text: string, rangeStart: number, index: number): boolean {
  return /^\s*\d{1,2}$/.test(text.slice(rangeStart, index))
}

/** Narrows a range to its non-whitespace content, or null if there is none. */
function trimRange(text: string, range: CharRange): CharRange | null {
  let { start, end } = range
  while (start < end && WHITESPACE_CHAR.test(text[start])) start += 1
  while (end > start && WHITESPACE_CHAR.test(text[end - 1])) end -= 1
  return end > start ? { start, end } : null
}

function countWords(text: string): number {
  return (text.match(/\S+/g) || []).length
}

/**
 * Word positions inside a range, as offsets into the full text.
 */
function findWordRanges(text: string, range: CharRange): CharRange[] {
  const words: CharRange[] = []
  let index = range.start

  while (index < range.end) {
    while (index < range.end && WHITESPACE_CHAR.test(text[index])) index += 1
    if (index >= range.end) break

    const wordStart = index
    while (index < range.end && !WHITESPACE_CHAR.test(text[index])) index += 1
    words.push({ start: wordStart, end: index })
  }

  return words
}

/**
 * Splits the text into sentence-sized ranges, preserving exact offsets.
 *
 * Offsets have to be exact because the reader renders the gaps between chunks by
 * slicing the display text between one chunk's end and the next one's start. The
 * previous implementation split on a regex and then trimmed the pieces without
 * moving the indices, so the whitespace between two sentences was attributed to
 * neither and simply vanished from the rendered output.
 */
function findSentenceRanges(text: string): CharRange[] {
  const ranges: CharRange[] = []
  let start = 0
  let index = 0

  while (index < text.length) {
    const character = text[index]

    if (character === '\n' || character === '\r') {
      let end = index
      while (end < text.length && (text[end] === '\n' || text[end] === '\r')) end += 1
      ranges.push({ start, end })
      start = end
      index = end
      continue
    }

    if (SENTENCE_TERMINATORS.includes(character)) {
      // Neither a number's decimal point nor an abbreviation's period ends a
      // sentence. The abbreviation judgement is shared with the "each sentence on
      // its own line" setting so the two features cannot disagree.
      if (
        character === '.' &&
        (isNumericPeriod(text, index) ||
          isListNumberPeriod(text, start, index) ||
          isAbbreviationPeriod(text, index))
      ) {
        index += 1
        continue
      }

      let end = index + 1
      while (end < text.length && SENTENCE_TERMINATORS.includes(text[end])) end += 1
      while (end < text.length && TRAILING_PUNCTUATION.includes(text[end])) end += 1

      ranges.push({ start, end })
      start = end
      index = end
      continue
    }

    index += 1
  }

  if (start < text.length) {
    ranges.push({ start, end: text.length })
  }

  return ranges
}

/**
 * Cuts an over-long sentence into phrases at its own commas, semicolons and
 * colons. See MAX_CHUNK_WORDS for why.
 *
 * A separator only counts when it ends a word, which is what keeps Polish
 * decimals ("2,5 kg") and times ("12:30") intact - there the mark sits inside the
 * token. A sentence with no usable separator is returned whole.
 */
function splitRangeAtPhrases(text: string, range: CharRange): CharRange[] {
  const words = findWordRanges(text, range)

  if (words.length <= MAX_CHUNK_WORDS) {
    return [range]
  }

  const cutAfterWord: number[] = []
  for (let wordIndex = 0; wordIndex < words.length - 1; wordIndex += 1) {
    const lastCharacter = text[words[wordIndex].end - 1]
    if (PHRASE_SEPARATORS.includes(lastCharacter)) {
      cutAfterWord.push(wordIndex)
    }
  }

  if (cutAfterWord.length === 0) {
    return [range]
  }

  const pieces: CharRange[] = []
  let firstWord = 0

  for (const cut of cutAfterWord) {
    const lengthSoFar = cut - firstWord + 1
    const remaining = words.length - (cut + 1)

    if (lengthSoFar >= TARGET_CHUNK_WORDS && remaining >= MIN_CHUNK_WORDS) {
      pieces.push({ start: words[firstWord].start, end: words[cut].end })
      firstWord = cut + 1
    }
  }

  pieces.push({ start: words[firstWord].start, end: range.end })

  return pieces
}

function createBoundary(
  chunkText: string,
  range: CharRange,
  startWordIndex: number,
  wordCount: number,
  language: SpeechLanguage
): SentenceBoundary {
  // ttsText is computed on first access, not upfront: a seventeen-page document
  // holds hundreds of chunks and cleaning all of them before the first word is
  // spoken is a delay the reader can hear.
  let cachedTtsText: string | null = null

  return {
    startCharIndex: range.start,
    endCharIndex: range.end,
    startWordIndex,
    endWordIndex: startWordIndex + wordCount - 1,
    text: chunkText,
    get ttsText(): string {
      if (cachedTtsText === null) {
        cachedTtsText = cleanTextForTTS(chunkText, language)
      }
      return cachedTtsText
    },
  } as SentenceBoundary
}

/**
 * Splits text into speech chunks: one utterance, and one highlight unit, each.
 *
 * Chunks are sentences - split on line ends and on terminators that are not part
 * of an abbreviation or a number - with any sentence longer than MAX_CHUNK_WORDS
 * cut further at its own phrase punctuation.
 *
 * Page break tokens are dropped so they are never spoken; the reader renders them
 * separately from the gaps between chunks.
 */
export function findSentenceBoundaries(
  text: string,
  language: SpeechLanguage = DEFAULT_SPEECH_LANGUAGE
): SentenceBoundary[] {
  if (!text.trim()) return []

  const boundaries: SentenceBoundary[] = []
  let wordIndex = 0

  for (const sentenceRange of findSentenceRanges(text)) {
    const sentence = trimRange(text, sentenceRange)
    if (!sentence) continue

    for (const phraseRange of splitRangeAtPhrases(text, sentence)) {
      const phrase = trimRange(text, phraseRange)
      if (!phrase) continue

      const chunkText = text.slice(phrase.start, phrase.end)
      if (isSkippableLine(chunkText)) continue

      const wordCount = countWords(chunkText)
      if (wordCount === 0) continue

      boundaries.push(createBoundary(chunkText, phrase, wordIndex, wordCount, language))
      wordIndex += wordCount
    }
  }

  return boundaries
}

/**
 * Create utterances for each sentence - one utterance per sentence
 */
export const createSentenceUtterances = (
  text: string,
  language: SpeechLanguage = DEFAULT_SPEECH_LANGUAGE
): SentenceBoundary[] => {
  return findSentenceBoundaries(text, language)
}

/**
 * Calculate word count efficiently without splitting the entire text
 */
export const getWordCount = (text: string): number => {
  if (!text.trim()) return 0
  // Count word boundaries (spaces between words) + 1
  let count = 1
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ' ' && i + 1 < text.length && text[i + 1] !== ' ') {
      count++
    }
  }
  return count
}

/**
 * Calculate word index from char index efficiently without splitting
 * Boundary events fire at the START of a word, so we want to return the index of that word
 */
export const getWordIndexFromCharIndex = (charIndex: number, textToUse: string): number => {
  if (charIndex <= 0) return 0
  if (charIndex >= textToUse.length) {
    return Math.max(0, getWordCount(textToUse) - 1)
  }
  
  // Count words up to and including the charIndex position
  let wordIndex = 0
  let inWord = false
  
  for (let i = 0; i <= charIndex && i < textToUse.length; i++) {
    if (textToUse[i] === ' ') {
      if (inWord) {
        inWord = false
      }
    } else {
      if (!inWord) {
        // We're starting a new word
        if (i <= charIndex) {
          wordIndex++
        }
        inWord = true
      }
    }
  }
  
  // Return the word index (0-based) of the word at charIndex
  // Since boundary events fire at word start, this is the word being spoken
  return Math.max(0, wordIndex - 1)
}

/**
 * Split text into chunks at word boundaries
 */
export const splitTextIntoChunks = (text: string, maxLength: number = MAX_UTTERANCE_LENGTH): Array<{ text: string, wordStartIndex: number }> => {
  if (text.length <= maxLength) {
    return [{ text, wordStartIndex: 0 }]
  }
  
  const chunks: Array<{ text: string, wordStartIndex: number }> = []
  let currentIndex = 0
  let wordStartIndex = 0
  
  while (currentIndex < text.length) {
    // Skip leading spaces
    while (currentIndex < text.length && text[currentIndex] === ' ') {
      currentIndex++
    }
    
    if (currentIndex >= text.length) break
    
    const remainingText = text.substring(currentIndex)
    
    if (remainingText.length <= maxLength) {
      // Last chunk - take everything (trimmed)
      chunks.push({ text: remainingText.trim(), wordStartIndex })
      break
    }
    
    // Find the last space before maxLength to split at word boundary
    let splitIndex = maxLength
    for (let i = maxLength - 1; i >= Math.max(0, maxLength - 200); i--) {
      if (remainingText[i] === ' ') {
        splitIndex = i
        break
      }
    }
    
    // If no space found, force split (shouldn't happen often with reasonable maxLength)
    if (splitIndex === maxLength) {
      splitIndex = maxLength - 1
    }
    
    const chunkText = remainingText.substring(0, splitIndex).trim()
    if (chunkText.length > 0) {
      chunks.push({ text: chunkText, wordStartIndex })
      
      // Calculate word start index for next chunk
      const wordsInChunk = getWordCount(chunkText)
      wordStartIndex += wordsInChunk
    }
    
    currentIndex += splitIndex
  }
  
  return chunks
}
